import { get } from "lodash";

import { compileSchema } from "./compile";
import { FieldNode, Schema } from "./types";

/*
 * The API returns a stored secret as a mask of asterisks. On update, it puts the stored secret
 * back where it gets a mask at the same path of an object. So a mask keeps its secret only where
 * the record as loaded has a mask too, with the same provider. Any other mask is saved as is.
 */

export const isMaskedSecret = (value: unknown): value is string =>
  typeof value === "string" && /^\*+$/.test(value);

export interface MaskedSecret {
  path: string;
  kept: boolean;
}

const join = (path: string, key: string) => (path ? `${path}.${key}` : key);

const collect = (
  node: FieldNode,
  value: unknown,
  stored: unknown,
  path: string,
  found: MaskedSecret[],
) => {
  switch (node.kind) {
    case "primitive":
      if (node.input === "password" && isMaskedSecret(value)) {
        found.push({ path, kept: isMaskedSecret(stored) });
      }
      break;
    case "object":
      node.children.forEach((child) =>
        collect(
          child,
          get(value, child.key),
          get(stored, child.key),
          join(path, child.key),
          found,
        ),
      );
      break;
    case "oneOf": {
      const provider = get(value, node.discriminator);
      const option = node.options.find((item) => item.value === provider);
      const sameProvider = get(stored, node.discriminator) === provider;
      if (option) {
        collect(
          option.node,
          value,
          sameProvider ? stored : undefined,
          path,
          found,
        );
      }
      break;
    }
    case "union":
      node.options.forEach((option) =>
        collect(option.node, value, stored, path, found),
      );
      break;
    // The form keeps stored secrets only in fixed fields, not in list or map entries
    case "array":
      if (Array.isArray(value)) {
        value.forEach((item, index) =>
          collect(node.item, item, undefined, join(path, String(index)), found),
        );
      }
      break;
    case "map":
      if (value && typeof value === "object") {
        Object.entries(value).forEach(([key, item]) =>
          collect(node.value, item, undefined, join(path, key), found),
        );
      }
      break;
  }
};

/*
 * Every password field of `value` that holds a mask, and whether the API keeps the stored secret for it.
 * `stored` is the record as loaded; a new record has none, so none of its masks is kept.
 */
export const findMaskedSecrets = (
  schema: Schema,
  value: unknown,
  stored?: unknown,
): MaskedSecret[] => {
  const found: MaskedSecret[] = [];
  collect(compileSchema(schema), value, stored, "", found);
  return found;
};
